import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SocialChannelEnum } from '@/app/common/general-types/briefs.dto';
import { SubmittedPostDto } from '@/app/common/general-types/submitted-posts.dto';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { SubmittedPostsService } from '@/app/services/submitted-posts/submitted-posts.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';

@Component({
  selector: 'ipr-submitted-post-fields',
  templateUrl: './submitted-post-fields.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'mt-3 block' },
})
export class SubmittedPostFields {
  private static readonly VIDEO_LINK_RESOLVE_DEBOUNCE_MS = 600;
  private static readonly EDIT_ROLES = [1, 4, 5, 6];

  readonly post = input.required<SubmittedPostDto>();
  readonly saved = output<SubmittedPostDto>();

  readonly videoLink = signal('');
  readonly adCode = signal('');
  readonly pendingFullUrl = signal<string | null>(null);
  readonly resolveLoading = signal(false);
  readonly resolveError = signal<string | null>(null);
  readonly saveLoading = signal(false);

  private videoLinkResolveTimer: ReturnType<typeof setTimeout> | undefined;
  private hydratedPostId: string | undefined;

  private readonly submittedPostsService = inject(SubmittedPostsService);
  private readonly toastNotificationService = inject(ToastNotificationService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.destroyRef.onDestroy(() => this.clearResolveTimer());

    effect(() => {
      const post = this.post();
      untracked(() => {
        if (this.hydratedPostId !== post.id) {
          this.hydrateFromPost(post);
        }
      });
    });
  }

  canEditPosts(): boolean {
    return this.currentUserService.hasAnyRole(SubmittedPostFields.EDIT_ROLES);
  }

  isInstagramStory(): boolean {
    return this.post().socialChannel === SocialChannelEnum['Instagram Story'];
  }

  getSavedFullUrl(): string {
    return this.post().fullUrl?.trim() ?? '';
  }

  getDisplayedFullUrl(): string {
    return this.pendingFullUrl() ?? this.getSavedFullUrl();
  }

  hasUnsavedFullUrlPreview(): boolean {
    const pending = this.pendingFullUrl();
    return pending !== null && pending !== this.getSavedFullUrl();
  }

  onVideoLinkInputEvent(event: Event): void {
    this.onVideoLinkInput((event.target as HTMLInputElement).value);
  }

  onAdCodeInputEvent(event: Event): void {
    this.onAdCodeInput((event.target as HTMLInputElement).value);
  }

  onVideoLinkInput(value: string): void {
    this.videoLink.set(value);
    this.scheduleFullUrlResolve(value);
  }

  onAdCodeInput(value: string): void {
    this.adCode.set(value);
  }

  viewPost(): void {
    const url = this.videoLink().trim() || this.post().videoLink;
    if (url) {
      window.open(url, '_blank');
    }
  }

  savePostFields(): void {
    const post = this.post();
    const videoLink = this.videoLink().trim();
    const adCode = this.adCode().trim();
    const pendingFullUrl = this.pendingFullUrl()?.trim() ?? '';
    const patch: Pick<SubmittedPostDto, 'videoLink' | 'adCode' | 'fullUrl'> = {
      videoLink,
      adCode,
    };
    if (pendingFullUrl) {
      patch.fullUrl = pendingFullUrl;
    }

    this.saveLoading.set(true);
    this.cdr.markForCheck();

    this.submittedPostsService
      .patchSubmittedPost(post.id, patch)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updatedPost) => {
          this.saveLoading.set(false);
          const nextPost = {
            ...post,
            ...updatedPost,
            videoLink: updatedPost.videoLink ?? videoLink,
            adCode: updatedPost.adCode ?? adCode,
            fullUrl: updatedPost.fullUrl ?? (pendingFullUrl || post.fullUrl),
          };
          this.hydrateFromPost(nextPost);
          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: 'Submitted post updated',
            message: 'Saved fullUrl, videoLink, and adCode changes.',
            type: 'success',
            icon: 'success',
          });
          this.saved.emit(nextPost);
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.saveLoading.set(false);
          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: 'Update failed',
            message: this.extractErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  private hydrateFromPost(post: SubmittedPostDto): void {
    this.hydratedPostId = post.id;
    this.videoLink.set(post.videoLink ?? '');
    this.adCode.set(post.adCode ?? '');
    this.pendingFullUrl.set(null);
    this.resolveLoading.set(false);
    this.resolveError.set(null);
    this.clearResolveTimer();
  }

  private scheduleFullUrlResolve(value: string): void {
    this.clearResolveTimer();

    const trimmed = value.trim();
    if (!trimmed) {
      this.resolveLoading.set(false);
      this.pendingFullUrl.set(null);
      this.resolveError.set(null);
      this.cdr.markForCheck();
      return;
    }

    const savedVideoLink = (this.post().videoLink ?? '').trim();
    if (trimmed === savedVideoLink) {
      this.pendingFullUrl.set(null);
      this.resolveLoading.set(false);
      this.resolveError.set(null);
      this.cdr.markForCheck();
      return;
    }

    this.pendingFullUrl.set(null);
    this.resolveLoading.set(true);
    this.resolveError.set(null);
    this.cdr.markForCheck();

    this.videoLinkResolveTimer = setTimeout(() => {
      this.videoLinkResolveTimer = undefined;
      this.resolveLoading.set(true);
      this.resolveError.set(null);
      this.cdr.markForCheck();

      this.submittedPostsService
        .resolveVideoLink(trimmed, this.post().socialChannel)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (resolved) => {
            this.resolveLoading.set(false);
            this.pendingFullUrl.set(resolved.fullUrl);
            this.resolveError.set(null);
            this.cdr.markForCheck();
          },
          error: (err: { error?: { message?: string | string[] }; message?: string }) => {
            this.resolveLoading.set(false);
            this.resolveError.set(this.extractErrorMessage(err));
            this.cdr.markForCheck();
          },
        });
    }, SubmittedPostFields.VIDEO_LINK_RESOLVE_DEBOUNCE_MS);
  }

  private clearResolveTimer(): void {
    if (this.videoLinkResolveTimer) {
      clearTimeout(this.videoLinkResolveTimer);
      this.videoLinkResolveTimer = undefined;
    }
  }

  private extractErrorMessage(err: {
    error?: { message?: string | string[] };
    message?: string;
  }): string {
    const nested = err.error?.message;
    if (Array.isArray(nested)) {
      return nested.join(' ');
    }
    return nested || err.message || 'Something went wrong.';
  }
}
